/**
 * The full record of one incident: header (status, headline, lineage links,
 * close reason), the AI agent panel, and its notifications, snoozes and
 * detections. Hosted by the detail modal over the project map and by the
 * standalone /log page.
 */
import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsPausedIcon from '@mui/icons-material/NotificationsPaused';
import PlaceIcon from '@mui/icons-material/Place';

import { useIncidentDetail } from '../../api/incidentLog';
import { useIncidentAgentSession } from '../../api/agentSessions';
import AgentSessionPanel from '../AgentSessionPanel';
import IncidentStatusChip from './IncidentStatusChip';
import IncidentLinkChips from './IncidentLinkChips';
import IncidentLinkOverrideBar from './IncidentLinkOverrideBar';
import AutomationRunRow from './AutomationRunRow';
import { kindIcon } from './incidentBits';
import {
    extraKinds,
    formatWhen,
    incidentTitle,
    linkedIncidentSummary,
    rekindleExplanation,
} from '../../utils/incidentRekindle';
import { realtimeKindLabel } from '../../utils/realtimeKindLabels';

function deliveryChipColor(status: string): 'success' | 'error' | 'warning' | 'default' {
    if (status === 'sent') return 'success';
    if (status === 'failed') return 'error';
    if (status.startsWith('skipped')) return 'warning';
    return 'default'; // queued / suppressed_*
}

/**
 * A titled panel whose *body* scrolls. `grow` panels take the remaining
 * column height (desktop); fixed panels cap themselves and scroll inside.
 */
function Section({
    title,
    grow = false,
    children,
}: {
    title: string;
    grow?: boolean;
    children: React.ReactNode;
}) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                ...(grow
                    ? { flexGrow: 1, maxHeight: { xs: 480, lg: 'none' } }
                    : { flexShrink: 0, maxHeight: 240 }),
            }}
        >
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                {title}
            </Typography>
            <Box sx={{ overflowY: 'auto', minHeight: 0, pr: 0.5 }}>{children}</Box>
        </Paper>
    );
}

export default function IncidentDetail({
    projectId,
    incidentId,
    onSelectIncident,
    fillHeight = true,
}: {
    projectId: string;
    incidentId: string;
    /** Same-project lineage links switch the view to that incident. */
    onSelectIncident?: (incidentId: number) => void;
    /** Desktop: fit the viewport and scroll panels internally (page);
     *  false lets the host (a modal) scroll the whole thing. */
    fillHeight?: boolean;
}) {
    const { data, isLoading } = useIncidentDetail(projectId, incidentId);
    const detail = data?.data;
    // Same query the panel uses (React Query dedupes). Agent UI exists only
    // when the tenant is entitled (`enabled`) or an old session left a log.
    const { data: agentData } = useIncidentAgentSession(projectId, incidentId);
    const showAgent = !!(agentData?.data?.session || agentData?.data?.enabled);

    if (isLoading || !detail) {
        return (
            <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    const { incident, detections, detections_pruned, notifications, snoozes } = detail;
    const automationRuns = detail.automation_runs ?? [];

    return (
        <Box
            sx={{
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                height: fillHeight ? { xs: 'auto', lg: '100%' } : 'auto',
                minHeight: 0,
            }}
        >
            <Box sx={{ flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                    {kindIcon(incident.kind, 32)}
                    <Typography
                        variant="h5"
                        component="span"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                    >
                        #{incident.id}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                        {incidentTitle(incident)}
                    </Typography>
                    <IncidentStatusChip status={incident.status} size="medium" />
                    {extraKinds(incident).map((k) => (
                        <Chip key={k} size="small" variant="outlined" label={`+ ${realtimeKindLabel(k)}`} />
                    ))}
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                    {incident.headline}
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                    <IncidentLinkChips
                        incident={incident}
                        projectId={projectId}
                        onSelectIncident={onSelectIncident}
                        size="medium"
                    />
                </Box>
                <IncidentLinkOverrideBar incident={incident} projectId={projectId} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {incident.detections} detection{incident.detections === 1 ? '' : 's'} · first{' '}
                    {formatWhen(incident.first_detected_at)} · last {formatWhen(incident.last_detected_at)}{' '}
                    ·{' '}
                    {incident.users.length > 0
                        ? `notifying ${incident.users.join(', ')}`
                        : 'nobody notified'}
                </Typography>
                {incident.closed_at && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 1.5 }}>
                        Closed {formatWhen(incident.closed_at)} by{' '}
                        {incident.closed_by ?? 'the AI agent'}
                        {incident.close_reason ? `: ${incident.close_reason}` : '.'}
                    </Typography>
                )}
                {incident.predecessor_id !== null && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 1.5 }}>
                        {rekindleExplanation(incident)}
                    </Typography>
                )}
                {linkedIncidentSummary(incident) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 1.5 }}>
                        {linkedIncidentSummary(incident)}
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: showAgent ? { xs: '1fr', lg: '5fr 4fr' } : '1fr',
                    alignItems: 'stretch',
                    pb: { xs: 2, lg: 3 },
                }}
            >
                {showAgent && <AgentSessionPanel projectId={projectId} incidentId={incidentId} />}

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        minHeight: 0,
                    }}
                >
                    <Section title={notifications.length === 1 ? 'Notification' : 'Notifications'}>
                        {notifications.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {notifications.map((notification, index) => (
                                    <Box
                                        key={`${notification.user}-${notification.at}`}
                                        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                                    >
                                        <NotificationsActiveIcon
                                            fontSize="small"
                                            sx={{ color: 'secondary.main', mt: 0.25 }}
                                        />
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {notification.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Sent to {notification.user} at {formatWhen(notification.at)}.
                                                {index === 0
                                                    ? ' Later detections in this area merged into this event silently.'
                                                    : ' Announced later — the fire reached this subscriber’s alert radius.'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                                                {notification.deliveries.map((delivery) => (
                                                    <Chip
                                                        key={delivery.channel}
                                                        size="small"
                                                        color={deliveryChipColor(delivery.status)}
                                                        label={`${delivery.channel} · ${delivery.status.replace(/_/g, ' ')}`}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No notification recorded for this event.
                            </Typography>
                        )}
                    </Section>

                    <Section title="Snoozes">
                        {snoozes.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No snoozes during this event.
                            </Typography>
                        ) : (
                            snoozes.map((snooze, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <NotificationsPausedIcon fontSize="small" sx={{ color: '#f59e0b' }} />
                                    <Typography variant="body2">
                                        {snooze.action === 'snooze'
                                            ? `${snooze.user} snoozed ${realtimeKindLabel(incident.kind).toLowerCase()} alerts for ${snooze.hours ?? '?'} h`
                                            : `${snooze.user} resumed ${realtimeKindLabel(incident.kind).toLowerCase()} alerts`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatWhen(snooze.at)}
                                    </Typography>
                                </Box>
                            ))
                        )}
                    </Section>

                    {automationRuns.length > 0 && (
                        <Section title="Automatic simulations">
                            {automationRuns.map((run) => (
                                <AutomationRunRow key={run.id} run={run} />
                            ))}
                        </Section>
                    )}

                    <Section title={`Detections (${detections.length})`} grow>
                        {detections_pruned && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Detection rows older than the 72 h retention window have been
                                pruned; the event's counts and footprint are preserved above.
                            </Typography>
                        )}
                        {detections.length === 0 && !detections_pruned ? (
                            <Typography variant="body2" color="text.secondary">
                                No detection rows found.
                            </Typography>
                        ) : (
                            detections.map((detection) => (
                                <Box key={detection.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <PlaceIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {detection.lat.toFixed(4)}, {detection.lon.toFixed(4)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatWhen(detection.observed_at)}
                                    </Typography>
                                </Box>
                            ))
                        )}
                    </Section>
                </Box>
            </Box>
        </Box>
    );
}
